// Kyetech.jsx — ULTRA MODERN USER UI (ADMIN CONTROLLED)

import React, { useEffect, useState, useMemo } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import jsPDF from "jspdf";
import { motion, AnimatePresence } from "framer-motion";
import {
  PhoneCall,
  CheckCircle,
  Loader2,
  RefreshCcw,
} from "lucide-react";

/* ===== NETWORK LOGOS ===== */
import mtnLogo from "../assets/networks/mtn.png";
import airteltigoLogo from "../assets/networks/airteltigo.png";
import telecelLogo from "../assets/networks/telecel.png";

/* ================= CONFIG ================= */
const PAYSTACK_FEE_RATE = 0.02;

const ADMIN_PHONE =
  import.meta.env?.VITE_ADMIN_PHONE ||
  process.env.REACT_APP_ADMIN_PHONE ||
  "233551006708";

/* ================= NETWORK PREFIXES ================= */
const MTN_PREFIXES = ["024", "054", "055", "059", "025", "053"];
const AIRTELTIGO_PREFIXES = ["026", "027", "056", "057"];

/* ================= NEW — ADMIN COLLECTION HELPER ================= */
const getPackageCollection = (network) => {
  switch (network) {
    case "MTN":
      return "packages_MTN";
    case "AirtelTigo":
      return "packages_AirtelTigo";
    case "Telecel":
      return "packages_Telecel";
    default:
      return "packages_MTN";
  }
};

/* ================= NETWORK STATUS DOT (MOBILE SMALL) ================= */
const NetworkStatusBadge = ({ status }) => {
  const map = {
    good: {
      color: "bg-green-500",
      animation: "animate-pulse",
    },
    slow: {
      color: "bg-yellow-400",
      animation: "animate-pulse",
    },
    down: {
      color: "bg-red-500",
      animation: "animate-ping",
    },
  };

  const current = map[status] || {
    color: "bg-gray-400",
    animation: "",
  };

  return (
    <span
      title={status?.toUpperCase() || "UNKNOWN"}
      className={`
        relative inline-flex
        
        /* 📱 Tiny dot */
        h-2.5 w-2.5
        
        /* 💻 Slightly bigger on desktop */
        md:h-3 md:w-3
        
        rounded-full
        ${current.color}
        
        ${current.animation}
        md:animate-none
      `}
    />
  );
};


/* ================= STATUS UI ================= */
const StatusBadge = ({ status }) => {
  if (status === "pending") {
    return (
      <div className="flex items-center justify-center gap-2 text-yellow-400 font-bold">
        <Loader2 size={18} className="animate-spin" />
        Pending
      </div>
    );
  }

  if (status === "processing") {
    return (
      <div className="flex items-center justify-center gap-2 text-blue-400 font-bold">
        <RefreshCcw size={18} className="animate-spin" />
        Processing
      </div>
    );
  }

  if (status === "completed") {
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 220 }}
        className="flex items-center justify-center gap-2 text-green-400 font-bold"
      >
        <CheckCircle size={18} />
        Completed
      </motion.div>
    );
  }

  return <span className="text-gray-400">{status}</span>;
};

/* ================= COMPONENT ================= */
export default function Kyetech() {
  /* ADMIN DATA */
  const [packages, setPackages] = useState([]);
  const [packagesActive, setPackagesActive] = useState(true);

  /* NETWORK FILTER */
  const [selectedNetwork, setSelectedNetwork] = useState("MTN");

  /* USER STATES */
  const [phone, setPhone] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [showSheet, setShowSheet] = useState(false);
  const [networkStatus, setNetworkStatus] = useState({});


  /* NETWORK DETECTION */
  const [detectedNetwork, setDetectedNetwork] = useState("");
  const [finalNetwork, setFinalNetwork] = useState("");
  const [isPorted, setIsPorted] = useState(false);

  /* STATUS CHECK INPUT */
  const [showTopPhoneInput, setShowTopPhoneInput] = useState(false);

  /* DATE FILTER */
  const todayISO = new Date().toISOString().split("T")[0];
  const [filterDate, setFilterDate] = useState(todayISO);

  const PAYSTACK_KEY =
    import.meta.env?.VITE_PAYSTACK_KEY ||
    process.env.REACT_APP_PAYSTACK_KEY;

  const apiKey =
    import.meta.env?.VITE_MNOTIFY_KEY ||
    process.env.REACT_APP_MNOTIFY_KEY ||
    "6kq4HAq6Tjd1qP4yaEDFbZ6OY";

  const senderId = "Bundgha";

  /* ================= HELPERS ================= */
  const formatGhs = (x) => `GHS ${Number(x || 0).toFixed(2)}`;
  const isValidPhone = (v) => /^0\d{9}$/.test(v);

  const normalizePhone = (p) =>
    p?.startsWith("0") ? "233" + p.slice(1) : p;

  const generateRandomEmail = () =>
    `user-${Math.random().toString(36).slice(2, 10)}@gmail.com`;

  const calculateTotal = (price) => {
    const fee = price * PAYSTACK_FEE_RATE;
    return { totalPaid: price + fee };
  };

  /* ===== PACKAGE AVAILABILITY ===== */
  const getAvailability = (pkg) => {
    if (!packagesActive)
      return { text: "Unavailable", color: "bg-red-600" };
    if (pkg.outOfStock)
      return { text: "Out of Stock", color: "bg-red-600" };
    return { text: "Available", color: "bg-green-600" };
  };

  /* ================= GLOBAL SWITCH ================= */
  useEffect(() => {
    async function loadSwitch() {
      const snap = await getDoc(doc(db, "settings", "packages"));
      setPackagesActive(snap.exists() ? snap.data().packagesActive : true);
    }
    loadSwitch();
  }, []);

/* ================= LOAD NETWORK STATUS 🔥 ================= */
  useEffect(() => {
    async function loadNetworkStatus() {
      const snap = await getDoc(doc(db, "network_status", "current"));
      if (snap.exists()) setNetworkStatus(snap.data());
    }
    loadNetworkStatus();
  }, []);

  /* ================= LOAD PACKAGES ================= */
  useEffect(() => {
    async function loadPackages() {
      const colName = getPackageCollection(selectedNetwork);
      const snap = await getDocs(collection(db, colName));
      setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }
    loadPackages();
  }, [selectedNetwork]);

  /* ================= NETWORK AUTO-DETECT ================= */
  useEffect(() => {
    if (!isValidPhone(phone)) {
      setDetectedNetwork("");
      setFinalNetwork("");
      setIsPorted(false);
      return;
    }

    const prefix = phone.substring(0, 3);
    let detected = "";

    if (MTN_PREFIXES.includes(prefix)) detected = "MTN";
    if (AIRTELTIGO_PREFIXES.includes(prefix)) detected = "AirtelTigo";
    if (!detected) detected = "Telecel";

    setDetectedNetwork(detected);
    setFinalNetwork(isPorted ? selectedNetwork : detected);
  }, [phone, isPorted, selectedNetwork]);

  /* ================= FILTERED PACKAGES ================= */
  const filteredPackages = useMemo(() => {
    return packages
      .filter((p) => p.network === selectedNetwork)
      .map((p) => ({
        ...p,
        gb: Number(p.id.replace(/[^0-9]/g, "")),
      }))
      .filter((p) => p.gb >= 1 && p.gb <= 50)
      .sort((a, b) => a.gb - b.gb);
  }, [packages, selectedNetwork]);
  /* ===== FILTER TRANSACTIONS BY DATE ===== */
  const filteredTransactions = useMemo(() => {
    if (!transactions.length) return [];
    return transactions.filter((t) => {
      if (!t.assignedAt) return false;
      const d = t.assignedAt.toDate().toISOString().split("T")[0];
      return d === filterDate;
    });
  }, [transactions, filterDate]);

  /* ================= PAYMENT ================= */
  const startPayment = () => {
    const netState = networkStatus?.[selectedNetwork]?.status;

    if (netState === "down") {
      alert(`${selectedNetwork} network is currently DOWN`);
      return;
    }

    if (netState === "slow") {
      if (!window.confirm(`${selectedNetwork} network is slow. Continue?`))
        return;
    }

    if (!selectedPkg || !isValidPhone(phone)) return alert("Invalid details");

    setProcessing(true);
    setShowSheet(false);

    const { totalPaid } = calculateTotal(selectedPkg.price);

   window.PaystackPop.setup({
  key: PAYSTACK_KEY,
  email: generateRandomEmail(),
  amount: Math.round(totalPaid * 100),
  currency: "GHS",

  metadata: {
    custom_fields: [
      {
        display_name: "Phone Number",
        variable_name: "phone",
        value: phone,
      },
      {
        display_name: "Network",
        variable_name: "network",
        value: finalNetwork,
      },
      {
        display_name: "Package",
        variable_name: "package",
        value: selectedPkg.id,
      },
      {
        display_name: "Ported Number",
        variable_name: "ported",
        value: isPorted ? "YES" : "NO",
      },
    ],
  },

  callback: (res) => handleSuccess(res.reference),
  onClose: () => setProcessing(false),
}).openIframe();
  };

 const handleSuccess = async (ref) => {
  await addDoc(collection(db, "credentials"), {
    assignedTo: phone,
    network: finalNetwork,
    detectedNetwork,
    isPorted,
    status: "pending",
    packageId: selectedPkg.id,
    totalPaid: selectedPkg.price,
    assignedAt: serverTimestamp(),
    paystackRef: ref,
  });

  // ✅ Generate PDF Receipt
  generateReceipt(ref);

  // 📩 SEND SMS
  await sendSMS(
    phone,
    `
Your Order on ${phone} received and processing.
Delivery in 5–10 minutes.
Thank you for choosing ukonnectivity.site
`
  );

  await sendSMS(
    ADMIN_PHONE,
    `New Order Received
Amount: ${formatGhs(selectedPkg.price)}
Phone: ${phone}`
  );

  setProcessing(false);
  alert("Payment successful! Receipt downloaded.");
};


  /* ================= STATUS CHECK ================= */
  const checkStatus = async () => {
    if (!isValidPhone(phone)) return alert("Invalid phone number");

    setLoadingStatus(true);
    setTransactions([]);

    const snap = await getDocs(
      query(collection(db, "credentials"), where("assignedTo", "==", phone))
    );

    if (snap.empty) {
      alert("No transactions found");
      setLoadingStatus(false);
      return;
    }

    setTransactions(
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        assignedAt: d.data().assignedAt,
        date: d.data().assignedAt?.toDate().toLocaleString(),
      }))
    );

    setLoadingStatus(false);
  };


  /* ================= SMS ================= */
 async function sendSMS(to, msg) {
  try {
    const p = normalizePhone(to);
    if (!p) return;

    const res = await fetch(
      `https://api.mnotify.com/api/sms/quick?key=${apiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipient: [p],
          sender: senderId,
          message: msg.trim(),
        }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
      console.error("SMS FAILED:", data);
    } else {
      console.log("SMS SENT:", data);
    }
  } catch (err) {
    console.error("SMS ERROR:", err);
  }
}


  /* ================= RECEIPT ================= */
  function generateReceipt(ref) {
    const pdf = new jsPDF();
    pdf.text("UK Connectivity Receipt", 20, 20);
    pdf.text(`Phone: ${phone}`, 20, 40);
    pdf.text(`Package: ${selectedPkg.id}`, 20, 50);
    pdf.text(`Network: ${finalNetwork}`, 20, 60);
    pdf.text(`Ref: ${ref}`, 20, 70);
    pdf.save(`Receipt-${ref}.pdf`);
  }

 return (
    <div className="min-h-screen bg-slate-900 text-white p-5">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-sky-400 mb-2">
          Bundle Ghana
        </h1>

        {/* 🚀 JOIN AGENT – ENHANCED */}
<motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.5 }}
  className="mb-8 p-5 rounded-2xl bg-gradient-to-r from-yellow-500/20 via-sky-500/10 to-yellow-500/20 border border-yellow-400/40 backdrop-blur-md"
>
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
    {/* TEXT */}
    <div>
      <h3 className="text-lg font-extrabold text-yellow-400 flex items-center gap-2">
        <motion.span
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          🚀
        </motion.span>
        Login as Agent
      </h3>

      <p className="text-sm text-white/90 mt-1 max-w-xl">
        Become / Login an agent to access <span className="font-bold text-sky-400">
        affordable data packages</span>.  
        Click the call icon to Join Our Whatsapp Page.
      </p>
    </div>

    {/* ACTIONS */}
    <div className="flex items-center gap-4">
      {/* ACTIONS */}
<div className="flex flex-wrap items-center gap-4">
  {/* JOIN AGENT BUTTON */}
  <motion.a
    whileHover={{ scale: 1.08 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => (window.location.href = "/agent/dashboard")}
    target="_blank"
    rel="noopener noreferrer"
    className="px-6 py-3 bg-yellow-500 text-black font-extrabold rounded-xl shadow-lg hover:bg-yellow-400 transition"
  >
    Login/Join
  </motion.a>

 

  {/* WHATSAPP ADMIN ICON */}
  <motion.a
    href="https://chat.whatsapp.com/CS5W6hXfI3EI0rLZ2YEBaa"
    animate={{ scale: [1, 1.15, 1] }}
    transition={{ repeat: Infinity, duration: 1.6 }}
    className="p-4 rounded-full bg-sky-600 shadow-lg hover:bg-sky-500"
    title="Contact Admin on WhatsApp"
  >
    <PhoneCall className="text-white" />
  </motion.a>
</div>
    </div>
  </div>
</motion.div>

        {/* ===== ORDER STATUS CHECKER ===== */}
        <div className="flex gap-3 mb-6">
          {showTopPhoneInput && (
            <input
              className="p-4 rounded-xl w-full text-black font-semibold"
              placeholder="Enter number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={10}
            />
          )}

          <button
            onClick={() =>
              !showTopPhoneInput ? setShowTopPhoneInput(true) : checkStatus()
            }
            disabled={loadingStatus}
            className="px-6 py-4 bg-green-600 rounded-xl font-bold flex items-center gap-2"
          >
            {loadingStatus ? (
              <Loader2 className="animate-spin" />
            ) : (
              <CheckCircle size={18} />
            )}
            {loadingStatus
              ? "Checking..."
              : showTopPhoneInput
              ? "Check"
              : "Check Order Status"}
          </button>
        </div>

        {/* ===== DATE FILTER ===== */}
        {transactions.length > 0 && (
          <div className="flex gap-4 mb-4">
            <button
              onClick={() => setFilterDate(todayISO)}
              className="px-4 py-2 rounded bg-sky-600 font-bold"
            >
              Today
            </button>

            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="px-4 py-2 rounded text-black font-semibold"
            />
          </div>
        )}

        {/* ===== STATUS TABLE ===== */}
        {filteredTransactions.length > 0 && (
          <div className="overflow-x-auto mb-10">
            <table className="w-full border border-white/20 text-sm">
              <thead className="bg-white/10">
                <tr>
                  <th className="p-3 border">Date</th>
                  <th className="p-3 border">Number</th>
                  <th className="p-3 border">Package</th>
                  <th className="p-3 border">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((t) => (
                  <tr key={t.id} className="text-center">
                    <td className="p-2 border">{t.date}</td>
                    <td className="p-2 border">{t.assignedTo}</td>
                    <td className="p-2 border">{t.packageId}</td>
                    <td className="p-2 border">
                      <StatusBadge status={t.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
         {/* 🔥 CUTE ULTRA-MODERN NETWORK STATUS */}
<div className="flex flex-wrap items-center gap-3 mb-6">
  {["MTN", "AirtelTigo", "Telecel"].map((net) => {
    const status = networkStatus?.[net]?.status;

    const statusConfig = {
      good: {
        text: "Good Network",
        color: "text-green-400",
      },
      slow: {
        text: "Slow but Delivery",
        color: "text-yellow-400",
      },
      down: {
        text: "Bad Network",
        color: "text-red-400",
      },
    };

    const current =
      statusConfig[status] || {
        text: "Unknown",
        color: "text-gray-400",
      };

    return (
      <div
        key={net}
        className="
          flex items-center gap-2
          px-3 py-2
          rounded-xl
          bg-white/5
          backdrop-blur-md
          border border-white/30
          shadow-sm
          bg
        "
      >
        {/* tiny animated dot */}
        <NetworkStatusBadge status={status} />

        {/* text */}
        <div className="flex flex-col leading-tight">
          <span className="text-xs font-bold">{net}</span>
          <span
            className={`text-[11px] font-medium ${current.color}`}
          >
            {current.text}
          </span>
        </div>
      </div>
    );
  })}
</div>


        {/* ===== NETWORK LOGOS ===== */}
        <div className="flex gap-6 mb-6">
          {[{ name: "MTN", logo: mtnLogo },
            { name: "AirtelTigo", logo: airteltigoLogo },
            { name: "Telecel", logo: telecelLogo }].map((net) => (
            <button
              key={net.name}
              onClick={() => setSelectedNetwork(net.name)}
              className={`p-3 bg-white rounded-2xl border-2 transition ${
                selectedNetwork === net.name
                  ? "border-sky-500 scale-110"
                  : "border-transparent opacity-70"
              }`}
            >
              <img src={net.logo} alt={net.name} className="h-10" />
            </button>
          ))}
        </div>

        {/* ===== PACKAGES ===== */}
        <div className="grid md:grid-cols-4 gap-5">
          {filteredPackages.map((pkg) => {
            const availability = getAvailability(pkg);
            return (
              <motion.div
                key={pkg.id}
                whileHover={{ scale: 1.05 }}
                onClick={() => {
                  if (!pkg.outOfStock && packagesActive) {
                    setSelectedPkg(pkg);
                    setShowSheet(true);
                  }
                }}
                className={`p-5 rounded-xl border bg-white/10 ${
                  pkg.outOfStock || !packagesActive
                    ? "opacity-40 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
              >
                <h3 className="font-bold">{pkg.id}</h3>
                <p className="text-xs">{pkg.description}</p>
                <p className="text-xl text-sky-400 mt-2">
                  {formatGhs(pkg.price)}
                </p>

                <div
                  className={`inline-block mt-3 px-3 py-1 text-xs font-bold rounded-full text-white ${availability.color}`}
                >
                  {availability.text}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* ===== PAYMENT SHEET ===== */}
      <AnimatePresence>
        {showSheet && selectedPkg && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            className="fixed bottom-0 left-0 right-0 bg-slate-800 p-6 rounded-t-3xl"
          >
            <h3 className="font-bold mb-2">Confirm Purchase</h3>

            <p className="text-sky-400 text-xl">
              {formatGhs(selectedPkg.price)}
            </p>

            {detectedNetwork &&
              detectedNetwork !== selectedNetwork && (
                <label className="flex items-center gap-3 mt-4 bg-yellow-500/20 p-3 rounded-xl text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={isPorted}
                    onChange={(e) => setIsPorted(e.target.checked)}
                  />
                  This {detectedNetwork} number is ported to{" "}
                  <span className="text-sky-400">{selectedNetwork}</span>
                </label>
              )}

            <input
              className="w-full p-4 mt-4 rounded text-black"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={10}
            />

            <button
              onClick={startPayment}
              disabled={processing}
              className="w-full mt-4 bg-sky-600 py-4 rounded font-bold"
            >
              {processing ? "Processing..." : "Confirm & Pay"}
            </button>

            <button
              onClick={() => setShowSheet(false)}
              className="w-full mt-3 border py-3 rounded"
            >
              Cancel
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}